/**
 * The filters of one report (batch 8 T14c, #3036). Every control the API accepts for that report is here; the rarely
 * used scope filters sit in "Advanced filters". Nothing is applied while the user types: the workbench sends a
 * request only when the report is run. Entities are chosen by name; ids are never typed or shown.
 */
import { useState, type ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { PRICE_KINDS, useReportCategories, useReportWarehouses, type ReportKind } from '@/hooks/useInventoryReports';
import { MultiLookup, SingleLookup } from './LookupField';
import { DEAD_SLOW_STATUSES, MAX_PART_IDS, type FieldErrors, type FilterState, type Picked } from './reportFilters';
import type { ReportsAccess } from './reportsSession';

export interface ReportFilterFormProps {
  kind: ReportKind;
  state: FilterState;
  errors: FieldErrors;
  access: ReportsAccess;
  onChange: (patch: Partial<FilterState>) => void;
}

const FIELD_SX = { minWidth: 180, flex: '1 1 180px' } as const;

export function ReportFilterForm({ kind, state, errors, access, onChange }: ReportFilterFormProps) {
  const { t } = useTranslation('inventoryReports');
  const errorText = (field: string): string | undefined => {
    const code = errors[field];
    return code === undefined ? undefined : t(`validation.${field === 'to' && code === 'order' ? 'order' : code}`);
  };
  const dateField = (field: 'from' | 'to') => (
    <TextField
      key={field}
      sx={FIELD_SX}
      size="small"
      type="date"
      label={t(`filters.${field}`)}
      value={state[field]}
      onChange={(event) => { onChange({ [field]: event.target.value }); }}
      error={errors[field] !== undefined}
      helperText={errorText(field) ?? t('filters.dateHint')}
      slotProps={{ inputLabel: { shrink: true } }}
    />
  );
  const intField = (field: 'deadDays' | 'slowCoverageDays' | 'windowDays', max: number) => (
    <TextField
      key={field}
      sx={FIELD_SX}
      size="small"
      label={t(`filters.${field}`)}
      value={state[field]}
      onChange={(event) => { onChange({ [field]: event.target.value }); }}
      error={errors[field] !== undefined}
      helperText={errorText(field) ?? t('filters.intHint', { max })}
      slotProps={{ htmlInput: { inputMode: 'numeric' } }}
    />
  );
  const basisOptions: readonly FilterState['basis'][] = !access.canFinancial ? ['units'] : kind === 'abc' ? ['cost', 'units', 'revenue'] : ['cost', 'units'];
  const basis = (
    <TextField
      select
      sx={FIELD_SX}
      size="small"
      label={t('filters.basis')}
      value={basisOptions.includes(state.basis) ? state.basis : (basisOptions[0] ?? 'units')}
      onChange={(event) => { onChange({ basis: event.target.value as FilterState['basis'] }); }}
      helperText={access.canFinancial ? undefined : t('filters.basisUnitsOnly')}
      disabled={!access.canFinancial}
    >
      {basisOptions.map((option) => (
        <MenuItem key={option} value={option}>{t(`filters.basisOptions.${option}`)}</MenuItem>
      ))}
    </TextField>
  );
  const groupBy = (options: readonly string[]) => (
    <TextField
      select
      sx={FIELD_SX}
      size="small"
      label={t('filters.groupBy')}
      value={state.groupBy}
      onChange={(event) => { onChange({ groupBy: event.target.value }); }}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>{t(`filters.groupByOptions.${option}`)}</MenuItem>
      ))}
    </TextField>
  );
  const scope = (
    <Advanced>
      <CategoryField value={state.category} onChange={(category) => { onChange({ category }); }} />
      <MultiLookup
        name="part"
        label={t('filters.parts')}
        value={state.parts}
        max={MAX_PART_IDS}
        onChange={(parts) => { onChange({ parts }); }}
        error={errorText('parts')}
      />
    </Advanced>
  );
  const warehouse = <WarehouseField value={state.warehouse} onChange={(next) => { onChange({ warehouse: next }); }} />;

  switch (kind) {
    case 'abc':
      return (
        <Form>
          {basis}
          {dateField('from')}
          {dateField('to')}
          <Advanced>
            <TextField
              size="small"
              label={t('filters.aThreshold')}
              value={state.aThreshold}
              onChange={(event) => { onChange({ aThreshold: event.target.value }); }}
              error={errors['aThreshold'] !== undefined}
              helperText={errorText('aThreshold') ?? t('filters.thresholdHint')}
            />
            <TextField
              size="small"
              label={t('filters.bThreshold')}
              value={state.bThreshold}
              onChange={(event) => { onChange({ bThreshold: event.target.value }); }}
              error={errors['bThreshold'] !== undefined}
              helperText={errorText('bThreshold') ?? t('filters.thresholdHint')}
            />
            <CategoryField value={state.category} onChange={(category) => { onChange({ category }); }} />
            <MultiLookup name="part" label={t('filters.parts')} value={state.parts} max={MAX_PART_IDS} onChange={(parts) => { onChange({ parts }); }} error={errorText('parts')} />
          </Advanced>
        </Form>
      );
    case 'turnover':
      return (
        <Form>
          {basis}
          {groupBy(['part', 'category'])}
          {dateField('from')}
          {dateField('to')}
          {scope}
        </Form>
      );
    case 'dead-and-slow':
      return (
        <Form>
          <FormControlLabel
            control={<Switch checked={access.canFinancial && state.includeValue} disabled={!access.canFinancial} onChange={(event) => { onChange({ includeValue: event.target.checked }); }} />}
            label={t('filters.includeValue')}
          />
          {!access.canFinancial && <FormHelperText>{t('filters.financialOnly')}</FormHelperText>}
          {intField('deadDays', 3650)}
          {intField('slowCoverageDays', 3650)}
          {intField('windowDays', 731)}
          <FormControl component="fieldset" sx={FIELD_SX}>
            <FormLabel component="legend">{t('filters.statuses')}</FormLabel>
            <FormGroup row>
              {DEAD_SLOW_STATUSES.map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      size="small"
                      checked={state.statuses.includes(status)}
                      onChange={(event) => {
                        onChange({ statuses: event.target.checked ? [...state.statuses, status] : state.statuses.filter((s) => s !== status) });
                      }}
                    />
                  }
                  label={t(`dead.status.${status}`)}
                />
              ))}
            </FormGroup>
            <FormHelperText>{t('filters.statusesHint')}</FormHelperText>
          </FormControl>
          {warehouse}
          {scope}
        </Form>
      );
    case 'profitability':
      return (
        <Form>
          {groupBy(['part', 'category', 'booking'])}
          {dateField('from')}
          {dateField('to')}
          <Advanced>
            {access.canBookingFilter ? (
              <SingleLookup name="booking" label={t('filters.booking')} value={state.booking} onChange={(booking) => { onChange({ booking }); }} helperText={t('filters.bookingHint')} />
            ) : (
              <Typography variant="caption" color="text.secondary">{t('filters.bookingNeedsPermission')}</Typography>
            )}
            {access.canPartyFilter ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <TextField
                  select
                  size="small"
                  label={t('filters.partyKind')}
                  value={state.partyKind}
                  onChange={(event) => { onChange({ partyKind: event.target.value as FilterState['partyKind'], party: null }); }}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="person">{t('filters.partyKinds.person')}</MenuItem>
                  <MenuItem value="company">{t('filters.partyKinds.company')}</MenuItem>
                </TextField>
                <SingleLookup key={state.partyKind} name={state.partyKind} label={t('filters.party')} value={state.party} onChange={(party) => { onChange({ party }); }} />
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">{t('filters.partyNeedsPermission')}</Typography>
            )}
            <CategoryField value={state.category} onChange={(category) => { onChange({ category }); }} />
            <MultiLookup name="part" label={t('filters.parts')} value={state.parts} max={MAX_PART_IDS} onChange={(parts) => { onChange({ parts }); }} error={errorText('parts')} />
          </Advanced>
        </Form>
      );
    case 'supplier-performance':
      return (
        <Form>
          {dateField('from')}
          {dateField('to')}
          <SingleLookup name="supplier" label={t('filters.supplier')} value={state.supplier} onChange={(supplier) => { onChange({ supplier }); }} />
          <FormControlLabel
            control={<Switch checked={access.canFinancial && state.includePriceVariance} disabled={!access.canFinancial} onChange={(event) => { onChange({ includePriceVariance: event.target.checked }); }} />}
            label={t('filters.includePriceVariance')}
          />
          {!access.canFinancial && <FormHelperText>{t('filters.financialOnly')}</FormHelperText>}
        </Form>
      );
    case 'price-history':
      return (
        <Form>
          <SingleLookup name="part" label={t('filters.part')} value={state.part} onChange={(part) => { onChange({ part }); }} error={errorText('part')} helperText={t('filters.partRequired')} />
          <FormControl component="fieldset" error={errors['kinds'] !== undefined}>
            <FormLabel component="legend">{t('filters.kinds')}</FormLabel>
            <FormGroup row>
              {PRICE_KINDS.map((k) => (
                <FormControlLabel
                  key={k}
                  control={
                    <Checkbox
                      size="small"
                      checked={state.kinds.includes(k)}
                      disabled={k !== 'sell' && !access.canFinancial}
                      onChange={(event) => { onChange({ kinds: event.target.checked ? [...state.kinds, k] : state.kinds.filter((x) => x !== k) }); }}
                    />
                  }
                  label={t(`priceHistory.kind.${k}`)}
                />
              ))}
            </FormGroup>
            <FormHelperText>{errorText('kinds') ?? (access.canFinancial ? t('filters.kindsHint') : t('filters.financialOnlyKinds'))}</FormHelperText>
          </FormControl>
          {dateField('from')}
          {dateField('to')}
          <SingleLookup name="supplier" label={t('filters.supplier')} value={state.supplier} onChange={(supplier) => { onChange({ supplier }); }} helperText={t('filters.supplierPriceHint')} />
        </Form>
      );
    case 'valuation':
      return (
        <Form>
          {groupBy(['part', 'category', 'warehouse'])}
          <FormControl component="fieldset">
            <FormLabel component="legend">{t('filters.asOf')}</FormLabel>
            <RadioGroup row value={state.asOfMode} onChange={(event) => { onChange({ asOfMode: event.target.value as FilterState['asOfMode'] }); }}>
              <FormControlLabel value="current" control={<Radio size="small" />} label={t('filters.asOfModes.current')} />
              <FormControlLabel value="date" control={<Radio size="small" />} label={t('filters.asOfModes.date')} />
              <FormControlLabel value="instant" control={<Radio size="small" />} label={t('filters.asOfModes.instant')} />
            </RadioGroup>
            {state.asOfMode === 'current' && <FormHelperText>{t('filters.asOfCurrentHint')}</FormHelperText>}
          </FormControl>
          {state.asOfMode === 'date' && (
            <TextField
              sx={FIELD_SX}
              size="small"
              type="date"
              label={t('filters.asOfDate')}
              value={state.asOfDate}
              onChange={(event) => { onChange({ asOfDate: event.target.value }); }}
              error={errors['asOf'] !== undefined}
              helperText={errorText('asOf') ?? t('filters.asOfDateHint')}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
          {state.asOfMode === 'instant' && (
            <TextField
              sx={FIELD_SX}
              size="small"
              label={t('filters.asOfInstant')}
              value={state.asOfInstant}
              onChange={(event) => { onChange({ asOfInstant: event.target.value }); }}
              error={errors['asOf'] !== undefined}
              helperText={errorText('asOf') ?? t('filters.asOfInstantHint')}
            />
          )}
          {warehouse}
          {scope}
        </Form>
      );
  }
}

function Form({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
      {children}
    </Stack>
  );
}

function Advanced({ children }: { children: ReactNode }) {
  const { t } = useTranslation('inventoryReports');
  const [open, setOpen] = useState(false);
  return (
    <Accordion expanded={open} onChange={(_event, next) => { setOpen(next); }} disableGutters sx={{ flex: '1 1 100%' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="report-advanced-filters">
        <Typography>{t('filters.advanced')}</Typography>
      </AccordionSummary>
      <AccordionDetails id="report-advanced-filters">
        <Stack spacing={2}>{children}</Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function CategoryField({ value, onChange }: { value: Picked | null; onChange: (value: Picked | null) => void }) {
  const { t } = useTranslation('inventoryReports');
  const { options, isError } = useReportCategories();
  const sorted = [...options].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <TextField
      select
      size="small"
      label={t('filters.category')}
      value={value?.id ?? ''}
      onChange={(event) => {
        const found = sorted.find((o) => o.id === event.target.value);
        onChange(found === undefined ? null : { id: found.id, label: found.name });
      }}
      error={isError}
      helperText={isError ? t('lookup.failed') : undefined}
      sx={FIELD_SX}
    >
      <MenuItem value="">{t('filters.any')}</MenuItem>
      {sorted.map((o) => (
        <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
      ))}
      {value !== null && !sorted.some((o) => o.id === value.id) && <MenuItem value={value.id}>{value.label}</MenuItem>}
    </TextField>
  );
}

function WarehouseField({ value, onChange }: { value: Picked | null; onChange: (value: Picked | null) => void }) {
  const { t } = useTranslation('inventoryReports');
  const { options, isError } = useReportWarehouses();
  return (
    <TextField
      select
      size="small"
      label={t('filters.warehouse')}
      value={value?.id ?? ''}
      onChange={(event) => {
        const found = options.find((o) => o.id === event.target.value);
        onChange(found === undefined ? null : { id: found.id, label: found.name });
      }}
      error={isError}
      helperText={isError ? t('lookup.failed') : undefined}
      sx={FIELD_SX}
    >
      <MenuItem value="">{t('filters.any')}</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
      ))}
      {value !== null && !options.some((o) => o.id === value.id) && <MenuItem value={value.id}>{value.label}</MenuItem>}
    </TextField>
  );
}

export function FilterErrorsAlert({ errors }: { errors: FieldErrors }) {
  const { t } = useTranslation('inventoryReports');
  if (Object.keys(errors).length === 0) return null;
  return (
    <Alert severity="error" role="alert">
      {t('validation.summary')}
    </Alert>
  );
}
